import React, { ReactNode, useContext, useMemo, useState } from 'react';
import { AuthContext, AuthContextValue } from '../context/AuthContext';
import { signInWithGoogle } from '../services/signInWithGoogle';
import { useLocation, Navigate } from 'react-router-dom';

export function useAuth(): AuthContextValue | null {
  return useContext(AuthContext);
}

const getUser = (): string | null => {
  return localStorage.getItem('user');
};

export function AuthProvider({ children }: Readonly<{ children: ReactNode }>) {
  const initialUser = getUser();
  const isUserExist = initialUser && initialUser !== 'undefined';
  const [user, setUser] = useState<Record<string, unknown> | null>(
    isUserExist ? JSON.parse(initialUser) : null,
  );

  // Memoized so consumers only re-render when the user changes.
  const value = useMemo<AuthContextValue>(() => {
    const signInWithGoogleFunc = async (code: string) => {
      const response = await signInWithGoogle.signInWithGoogle(code);

      if (response instanceof Error) {
        return response;
      }

      setUser(response);
      localStorage.setItem('user', JSON.stringify(response));

      return response;
    };

    return { user, setUser, signInWithGoogleFunc };
  }, [user]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

interface RequireAuthProps {
  children: ReactNode;
  redirect?: string;
}

export function RequireAuth({
  children,
  redirect = '/',
}: Readonly<RequireAuthProps>): React.ReactElement {
  const auth = useAuth();
  const location = useLocation();

  if (!auth?.user) {
    return <Navigate to={redirect} state={{ from: location }} replace />;
  }

  return <>{children}</>;
}
